import { useEffect, useState } from "react";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { NAV_LINKS } from "@/data/content";
import { scrollToId } from "@/lib/scroll";

const Logo = ({ onClick, testId }) => (
  <button data-testid={testId} onClick={onClick} className="group flex items-baseline gap-1 text-left" aria-label="Amara Studios home">
    <span className="font-serif text-xl sm:text-2xl tracking-[0.32em] text-charcoal transition-colors group-hover:text-sage">AMARA</span>
    <span className="font-mono text-[10px] tracking-[0.32em] text-taupe ml-1 hidden sm:inline">STUDIOS</span>
  </button>
);

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { scrollY } = useScroll();
  useMotionValueEvent(scrollY, "change", (v) => setScrolled(v > 40));

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 250 : 0);
  };

  return (
    <>
      <motion.header
        data-testid="nav-header"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${scrolled ? "glass border-b border-taupe-line/70" : "border-b border-transparent"}`}
      >
        <nav className="container-x flex items-center justify-between h-[72px]" aria-label="Primary">
          <Logo testId="nav-logo" onClick={() => go("home")} />
          <ul className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <li key={l.id}>
                <button data-testid={`nav-link-${l.id}`} onClick={() => go(l.id)} className="link-underline text-[13px] tracking-wide text-ink/80 hover:text-charcoal transition-colors">
                  {l.label}
                </button>
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-3">
            <button data-testid="nav-cta-button" onClick={() => go("contact")} className="btn btn-dark hidden sm:inline-flex !py-2.5 !px-6 text-[13px]">
              <span>Start Your Project</span>
            </button>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen((o) => !o)}
              className="lg:hidden relative h-10 w-10 flex flex-col items-center justify-center gap-[6px]"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
            >
              <motion.span animate={open ? { rotate: 45, y: 4 } : { rotate: 0, y: 0 }} className="block h-px w-6 bg-charcoal" />
              <motion.span animate={open ? { rotate: -45, y: -3 } : { rotate: 0, y: 0 }} className="block h-px w-6 bg-charcoal" />
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-40 bg-cream flex flex-col pt-28 pb-10 container-x lg:hidden"
          >
            <ul className="flex flex-col gap-1">
              {NAV_LINKS.map((l, i) => (
                <motion.li key={l.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25 + i * 0.05, duration: 0.6 }}>
                  <button data-testid={`mobile-nav-link-${l.id}`} onClick={() => go(l.id)} className="flex items-baseline gap-4 py-2.5 text-left w-full border-b border-taupe-line/70">
                    <span className="font-mono text-[10px] text-sage">0{i + 1}</span>
                    <span className="font-serif text-4xl text-charcoal">{l.label}</span>
                  </button>
                </motion.li>
              ))}
            </ul>
            <div className="mt-auto flex flex-col gap-4">
              <button data-testid="mobile-nav-cta" onClick={() => go("contact")} className="btn btn-dark w-full"><span>Start Your Project</span></button>
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-taupe">Bangalore, India</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
